<template>
  <div class="v-wrapper">
    <loading :isLoading="isLoading">

      <tag-description
        v-if="tagDescription && Object.keys(tagDescription).length"
        :tagDescription="tagDescription"
      />

      <div class="v-countNumber">{{ count }}{{ $t("ListPage.count") }}</div>

      <ring-list
        :rings="rings"
        :middleBanners="middleBanners"
        :middleBannerPositions="middleBannerPositions"
        :isNotFiltered="isNotFiltered"
        :favoritedRings="favoritedRings"
        :photoMode="photoMode"
        :bgMode="bgMode"
        :isFavoritedLoading="isFavoritedLoading"
        :productBackgrounds="productBackgrounds"
        :productBackgroundsRandom="productBackgroundsRandom"
        @ringClick="onRingClick"
        @quickViewClick="onQuickViewClick"
        @favoriteClick="onFavoriteClick"
      />

      <div>
        <button
          v-show="currentPage < totalPages"
          class="v-button primary"
          :style="moreLoading ? 'opacity: 0.5;' : ''"
          :disabled="moreLoading"
          @click="onLoadMoreClick"
        >もっと見る</button>
      </div>
    </loading>

    <modal v-show="isModalOpen" @close="onModalClose" @afterLeave="ringQuickView = null">
      <ring-quick-view v-if="ringQuickView" :ringQuickView="ringQuickView" />
    </modal>
  </div>
</template>

<script>
import { Http } from '../../../http'
import Loading from './Loading.vue'
import RingList from './RingList.vue'
import RingQuickView from './RingQuickView.vue'
import TagDescription from './TagDescription.vue'
import Modal from './Modal.vue'

export default {
  name: 'ListPage',

  props: {
    currentPage: { type: Number, required: true },
    selectedDesigns: { type: Array, required: true },
    selectedMetals: { type: Array, required: true },
    selectedTanzos: { type: Array, required: true },
    selectedTextures: { type: Array, required: true },
    selectedDiamonds: { type: Array, required: true },
    selectedTypes: { type: Array, required: true },
    selectedEc: { type: Array, required: true },
    photoMode: { type: String, required: true },
    bgMode: { type: String, required: true },
  },

  components: { Loading, RingList, RingQuickView, TagDescription,  Modal, },

  data () {
    return {
      pageTitle: null,
      pageDescription: null,
      canonical: null,
      titleJa: null,
      titleEn: null,
      count: 0,
      rings: [],
      middleBanners: [],
      middleBannerPositions: [6, 14, 22, 30, 38, 46, 54, 62, 70, 78, 86], // 指輪一覧のArray位置
      favoritedRings: [],
      isLoading: true,
      moreLoading: true,
      isFavoritedLoading: true,
      totalPages: 0,
      ringQuickView: null,
      isModalOpen: false,
      tagDescription: null,
      productBackgrounds: [],
      productBackgroundsRandom: ["5", "7", "8", "11", "13", "22", "23", "24", "28", "29", "51", "52"] // ランダム表示で使う画像
    }
  },

  watch: {
    pageTitle: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue) {
          document.title = newValue;
        }
      },
    },
    pageDescription: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue) {
          document.querySelector('meta[name="description"]').setAttribute("content", newValue);
        }
      },
    },
    canonical: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue) {
          document.querySelector('link[rel="canonical"]').setAttribute("href", newValue);
        }
      },
    },
    titleJa: {
      immediate: true,
      handler (newValue, oldValue) {
        const element = document.querySelector('.js-collectionTitleJa')
        if (newValue && element) {
          element.innerHTML = newValue;
        }
      },
    },
    titleEn: {
      immediate: true,
      handler (newValue, oldValue) {
        const element = document.querySelector('.js-collectionTitleEn')
        if (newValue && element) {
          element.innerHTML = newValue;
        }
      },
    },
  },

  methods: {
    isNotFiltered () {
      const allFilters = [
        this.selectedDesigns,
        this.selectedMetals,
        this.selectedTanzos,
        this.selectedTextures,
        this.selectedDiamonds,
        this.selectedTypes,
        this.selectedEc,
      ]

      return [].concat.apply([], allFilters).length == 0
    },
    getRings (nextPage = false) {
      const url = "/api/collections"
      const pathname = this.$pathname
      const query = {
        'design[]': this.selectedDesigns,
        'metal[]': this.selectedMetals,
        'tanzo[]': this.selectedTanzos,
        'texture[]': this.selectedTextures,
        'diamond[]': this.selectedDiamonds,
        'type[]': this.selectedTypes,
        'page': nextPage ? this.currentPage + 1 : this.currentPage,
        ...this.$route.query
      }
      const queryParams = this.$router.resolve({
        path: '',
        query: query,
      }).resolved.fullPath.substring(1)

      return Http.get(url + pathname + queryParams)
        .then(response => {
          this.totalPages = response.data.total_pages
          if (nextPage) {
            this.$emit('incrementCurrentPage')
          }
          this.count = response.data.count
          this.rings = this.rings.concat( response.data.rings )
          if (response.data.middle_banners) {
            this.middleBanners = response.data.middle_banners
          }
          this.isLoading = false
          this.moreLoading = false
          if (response.data.tag_description && Object.keys(response.data.tag_description).length) {
            this.tagDescription = response.data.tag_description
          }
          if (response.data.page_title) {
            this.pageTitle = response.data.page_title
          }
          if (response.data.page_description) {
            this.pageDescription = response.data.page_description
          }
          if (response.data.canonical) {
            this.canonical = response.data.canonical
          }
          if (response.data.title_ja) {
            this.titleJa = response.data.title_ja
          }
          if (response.data.title_en) {
            this.titleEn = response.data.title_en
          }
          this.productBackgrounds = response.data.product_backgrounds
        })
    },
    getRingFavorites () {
      const url = "/api/customer_favorites"
      Http.get(url)
        .then(response => {
          const favoritedRingIds = response.data.rings.map(r => r.ring_id)
          this.favoritedRings = JSON.parse(JSON.stringify(favoritedRingIds))
          this.isFavoritedLoading = false
        })
    },
    onLoadMoreClick () {
      this.moreLoading = true
      this.getRings(true)
    },
    onRingClick (e, ringObject) {
    },
    onQuickViewClick (e, ringObject) {
      this.isModalOpen = true
      const ring = JSON.parse(JSON.stringify(ringObject))
      this.ringQuickView = ring
    },
    onFavoriteClick (e, ringObject) {
      const isRingFavorited = this.favoritedRings.includes(ringObject.id)
      const url = "/api/customer_favorites"
      const data = { ring_id: ringObject.id }

      if (isRingFavorited) {
        Http.post(url, data, "DELETE")
          .then(response => {
            const ringId = response.data.ring.id
            this.favoritedRings = this.favoritedRings.filter(r => r !== ringId)
          })
      } else {
        Http.post(url, data)
          .then(response => {
            const ringId = response.data.ring.id
            this.favoritedRings = this.favoritedRings.concat(ringId)
          })
      }
    },
    onModalClose () {
      this.isModalOpen = false
    },
  },

  created () {
    this.getRings()
      .then(() => {
        this.getRingFavorites()
      })
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  padding: var(--contents-padding);
}

.v-countNumber {
  font-size: 10px;

  @media screen and (max-width: 899.9px) {
    position: fixed;
    bottom: env(safe-area-inset-bottom);
    right: 0;
    left: 0;
    z-index: 1000;
    margin: 0;
    padding: 5px;
    background: rgba(239, 239, 239, .9);
    text-align: center;
    letter-spacing: .1em;
    animation: showVCount .3s var(--animation-easing) 1 0s both;
  }

  @media screen and (min-width: 900px) {
    margin: 0 0 10px;
    height: 20px;
  }
}

@keyframes showVCount {
  0%   {opacity: 0;}
  100% {opacity: 1;}
}
</style>
